/**
 * SLICK SLIDER WEB COMPONENT
 * -------------------------
 * A flexible wrapper for Slick.js using Web Components.
 * * ATTRIBUTES:
 * - slides-to-show: (int) Base number of slides (Desktop)
 * - slides-to-scroll: (int) Number of slides to move per click
 * - button-style: (string) Theme variant: 'button--primary', 'button--secondary', 'button--tertiary', 'button--dark', 'button--white'
 * - button-size: (string) Theme variant: 'button-sm' - WIP, more to be added.
 * - arrows: (bool) Show navigation arrows
 * - dots: (bool) Show pagination dots
 * - infinite: (bool) Loop slides (Note: Arrows will NOT hide at ends if this is present)
 * * BREAKPOINTS:
 * Use <slider-breakpoint> children to define responsive behavior.
 * - width: (int) Max-width for this breakpoint
 * - show: (float) Slides to show at this width
 * - scroll: (int) Slides to scroll at this width
 * * EXAMPLE:
 * <slick-slider slides-to-show="4" button-style="button--secondary" arrows>
 * <slider-breakpoint width="1024" show="2.5"></slider-breakpoint>
 * <slider-breakpoint width="768" show="1.2"></slider-breakpoint>
 * <div class="slider-container"> ... slides ... </div>
 * </slick-slider>
 */

class SlickSlider extends HTMLElement {
  connectedCallback() {
    setTimeout(() => this.initSlider(), 10);
  }

  initSlider() {
    if (!window.jQuery || !jQuery.fn.slick || this.dataset.initialized) return;

    const $target = $(this).find('.slider-container');
    const isAuto = this.getAttribute('slides-to-show') === 'auto';
    
    const breakpointElements = this.querySelectorAll('slider-breakpoint');
    const responsiveSettings = Array.from(breakpointElements).map((el) => ({
      breakpoint: parseInt(el.getAttribute('width')),
      settings: {
        slidesToShow: isAuto ? 1 : (parseFloat(el.getAttribute('show')) || 1),
        slidesToScroll: parseInt(el.getAttribute('scroll')) || 1,
        variableWidth: isAuto 
      },
    }));

    const btnStyle = this.getAttribute('button-style') || 'button--primary';
    const btnSize = this.getAttribute('button-size') || '';

    const prevBtn = `<button type="button" class="slick-prev button ${btnStyle} button--rounded ${btnSize}" aria-label="Previous"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256"><path d="M165.66,202.34a8,8,0,0,1-11.32,11.32l-80-80a8,8,0,0,1,0-11.32l80-80a8,8,0,0,1,11.32,11.32L91.31,128Z"></path></svg></button>`;
    const nextBtn = `<button type="button" class="slick-next button ${btnStyle} button--rounded ${btnSize}" aria-label="Next"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256"><path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z"></path></svg></button>`;

    const settings = {
      variableWidth: isAuto,
      slidesToShow: isAuto ? 1 : (parseInt(this.getAttribute('slides-to-show')) || 3),
      slidesToScroll: parseInt(this.getAttribute('slides-to-scroll')) || 1,
      dots: this.hasAttribute('dots'),
      arrows: this.hasAttribute('arrows'),
      infinite: this.hasAttribute('infinite'),
      prevArrow: prevBtn,
      nextArrow: nextBtn,
      responsive: responsiveSettings,
    };

    $target.on('afterChange init', (event, slick) => {
      if (!slick.options.variableWidth || slick.options.infinite) return;

      const $lastSlide = $(slick.$slides[slick.slideCount - 1]);
      const sliderRect = slick.$slider[0].getBoundingClientRect();
      const lastSlideRect = $lastSlide[0].getBoundingClientRect();

      const isAtEnd = Math.floor(lastSlideRect.right) <= Math.ceil(sliderRect.right) + 10;

      if (isAtEnd) {
        slick.$nextArrow.addClass('slick-disabled').css({ 'opacity': '0', 'pointer-events': 'none' });
      } else {
        slick.$nextArrow.removeClass('slick-disabled').css({ 'opacity': '1', 'pointer-events': 'auto' });
      }
    });

    $target.slick(settings);
    $target.removeClass('is-loading');
    this.dataset.initialized = 'true';
  }
}

customElements.define('slick-slider', SlickSlider);